import * as Immutable from 'immutable';
import { handleActions } from 'redux-actions';

import { ENDPOINTS_USER } from '../../config';
import { chartMapping } from '../../shared/data/chartSeriesChange';
import { initialSelectedColumns } from '../../shared/data/initialColumnsTable';
import { getChartTable } from '../../shared/helpers';

import { alphasIntro, alphasOptions } from './AlphasOptionsIntro';
import { AlphasActionType, AlphasCorrelationFailureActionType, AlphasStoreElement, CorrelationSelfType } from './AlphasReducer.types';
import {
  formatPerformanceData,
  formatPerformanceStats,
  getAlphaDetail,
  getAlphaSummary,
  setAlphaProp,
  setCorrelationInAlphas,
  updateAlphasSummary,
} from './AlphasReducerHelpers';
import { getStartDates } from '../GenericSelectors';

export const initialSelectedColumnsAlphas = initialSelectedColumns();

// @ts-ignore
const initialStateForIntro: AlphasStoreElement = Immutable.fromJS({
  hasComeFromAlphaPage: false,
  myAlphas: alphasIntro,
  alphaDetail: null,
  options: alphasOptions,
  fetchingData: false,
  fetchingProperties: false,
  loadedOptions: false,
  showDetail: false,
  detailTabSelected: 0,
  selectedColumns: initialSelectedColumnsAlphas,
  fetchingCheck: false,
  fetchingRecordSetsChart: false,
  fetchingRecordSetsSummary: false,
  chartData: null,
  fetchingRecordSets: false,
  recordsSets: null,
  fetchingSubmitAlpha: false,
  type: 'unsubmitted',
  numOfUnsubmitted: 0,
  fetchNum: null,
  numOfActive: 0,
  fetchOSNum: false,
  fetchProdNum: false,
  numOfDecommissioned: 0,
  pageSizeUI: 10,
  controller: null,
  chartType: 'pnl',
  alphasPath: `${ENDPOINTS_USER.user}/self`,
  selfCorrelationData: null,
  fetchingSelfCorrelation: false,
  summaryTable: null,
  summaryOSPROD: null,
});

const createResponseAlphasCorrelationSuccessValues = (valueField: string, lastRunField: string, fetchingField: string) => {
  return (state: AlphasStoreElement, action: AlphasActionType<CorrelationSelfType>) => {
    const currentAlphaId = state.getIn(['alphaDetail', 'id']);

    if (action.payload.alphaId) {
      const results = state.getIn(['myAlphas', 'results']);
      const shouldSaveInAlphaDetail = action.payload.alphaId === currentAlphaId;

      // @ts-ignore
      const alphaIndex = results && results.findIndex((alpha: any) => alpha.get('id') === action.payload.alphaId);

      if (results && alphaIndex >= 0) {
        return state
          .setIn(['alphaDetail', valueField], shouldSaveInAlphaDetail ? Immutable.fromJS(action.payload.json) : undefined)
          .setIn(['myAlphas', 'results', alphaIndex, valueField], Immutable.fromJS(action.payload.json))
          .setIn(['myAlphas', 'results', alphaIndex, lastRunField], action.payload.date)
          .setIn(['alphaDetail', fetchingField], false)
          .setIn(['alphaDetail', lastRunField], shouldSaveInAlphaDetail ? action.payload.date : undefined);
      } else {
        return state
          .setIn(['alphaDetail', valueField], shouldSaveInAlphaDetail ? Immutable.fromJS(action.payload.json) : undefined)
          .setIn(['alphaDetail', fetchingField], false)
          .setIn(['alphaDetail', lastRunField], shouldSaveInAlphaDetail ? action.payload.date : undefined);
      }
    }

    return state;
  };
};

const createRequestAlphasCorrelation = (fetchingField: string) => {
  return (state: AlphasStoreElement) => {
    if (state.get('alphaDetail')) {
      return state.setIn(['alphaDetail', fetchingField], true);
    } else {
      return state;
    }
  };
};

const createResponseAlphasCorrelationFailure = (fetchingField: string) => {
  return (state: AlphasStoreElement, action: AlphasCorrelationFailureActionType) => {
    if (state.get('alphaDetail')) {
      return state.setIn(['alphaDetail', 'correlationStatusCode'], action.payload?.statusCode).setIn(['alphaDetail', fetchingField], false);
    } else {
      return state;
    }
  };
};

// @ts-ignore
const initialState: AlphasStoreElement = Immutable.fromJS({
  hasComeFromAlphaPage: false,
  myAlphas: null,
  alphaDetail: null,
  options: null,
  fetchingData: false,
  fetchingProperties: false,
  loadedOptions: false,
  showDetail: false,
  detailTabSelected: 0,
  selectedColumns: initialSelectedColumnsAlphas,
  fetchingCheck: false,
  fetchingRecordSetsChart: false,
  chartData: null,
  fetchingRecordSets: false,
  recordsSets: null,
  fetchingSubmitAlpha: false,
  type: 'unsubmitted',
  numOfIS: 0,
  fetchNum: null,
  numOfOS: 0,
  fetchOSNum: false,
  fetchProdNum: false,
  numOfProd: 0,
  pageSizeUI: 10,
  controller: null,
  chartType: 'pnl',
  alphasPath: `${ENDPOINTS_USER.user}/self`,
  selfCorrelationData: null,
  fetchingSelfCorrelation: false,
  hideActive: false,
  allActive: false,
  summaryTable: null,
  summaryOSPROD: null,
  currentAlphaId: null,
});

const AlphasReducer = handleActions<any>(
  {
    REQUEST_ALPHAS: (state: AlphasStoreElement, action: AlphasActionType): AlphasStoreElement =>
      state.merge(
        Immutable.fromJS({
          fetchingData: true,
          controller: action.payload,
        })
      ),
    RESPONSE_ALPHAS_SUCCESS: (state: AlphasStoreElement, action: AlphasActionType) => {
      const alphas: any = action.payload;
      alphas.results =
        alphas.results &&
        alphas.results.map((alpha: any) => ({
          ...alpha,
          performanceAlphaFetching: false,
          performanceAlphaSummary: getAlphaSummary(),
          performanceAlphaLastDate: null,
        }));

      return state.merge(
        Immutable.fromJS({
          myAlphas: alphas,
          fetchingData: false,
        })
      );
    },
    RESPONSE_ALPHAS_FAILURE: (state: AlphasStoreElement) =>
      state.merge(
        Immutable.fromJS({
          fetchingData: false,
          myAlphas: {
            count: 0,
            results: [],
          },
        })
      ),
    REQUEST_SUPER_ALPHAS: (state: AlphasStoreElement, action: AlphasActionType): AlphasStoreElement =>
      state.set('fetchingAlphaSelections', true).set('superController', action.payload),
    RESPONSE_SUPER_ALPHAS_SUCCESS: (state: AlphasStoreElement, action: AlphasActionType) =>
      state.set('fetchingAlphaSelections', false).set('alphaSelections', Immutable.fromJS(action.payload)),
    RESPONSE_SUPER_ALPHAS_FAILURE: (state: AlphasStoreElement) => state.set('fetchingAlphaSelections', false),
    RESPONSE_ALPHAS_OPTIONS: (state: AlphasStoreElement, action: AlphasActionType) => {
      const data: any = action.payload;
      data.competitions.choices.unshift({ value: 'true', label: 'Any' });
      data.competitions.choices.push({ value: 'false', label: 'None' });

      return state.set('options', Immutable.fromJS(data)).set('fetchingData', false).set('loadedOptions', true);
    },
    REQUEST_ALPHAS_OPTIONS: (state: AlphasStoreElement) =>
      state.merge(
        Immutable.fromJS({
          fetchingData: true,
        })
      ),
    RESPONSE_ALPHAS_OPTIONS_FAILURE: (state: AlphasStoreElement) =>
      state.merge(
        Immutable.fromJS({
          fetchingData: false,
          loadedOptions: false,
        })
      ),
    RESPONSE_UPDATE_ALPHA_PROPERTIES: (state: AlphasStoreElement) =>
      state.merge(
        Immutable.fromJS({
          fetchingProperties: false,
        })
      ),
    REQUEST_UPDATE_ALPHA_PROPERTIES: (state: AlphasStoreElement) =>
      state.merge(
        Immutable.fromJS({
          fetchingProperties: true,
        })
      ),
    RESPONSE_UPDATE_ALPHA_PROPERTIES_FAILURE: (state: AlphasStoreElement) =>
      state.merge(
        Immutable.fromJS({
          fetchingProperties: false,
        })
      ),
    RESPONSE_UPDATE_ALPHA: (state: AlphasStoreElement, action: AlphasActionType) => {
      const data: any = action.payload;
      let newMyAlphas: any;
      if (data.length !== undefined) {
        // @ts-ignore
        newMyAlphas = state.getIn(['myAlphas', 'results']).map((alpha: any) => {
          const alphaToUpdate = data.find((element: any) => element.id === alpha.get('id'));

          return alphaToUpdate ? Immutable.fromJS(alphaToUpdate) : alpha;
        });

        return state.setIn(['myAlphas', 'results'], newMyAlphas).merge(Immutable.fromJS({ fetchingProperties: false }));
      } else if (!state.get('myAlphas')) {
        return state.merge(Immutable.fromJS({ alphaDetail: data, fetchingProperties: false }));
      } else {
        // @ts-ignore
        const index = state.getIn(['myAlphas', 'results']).findIndex((alpha: any) => alpha.get('id') === data.id);
        if (index >= 0) {
          return state
            .setIn(['myAlphas', 'results', index], Immutable.fromJS(data))
            .merge(Immutable.fromJS({ alphaDetail: data, fetchingProperties: false }));
        } else {
          return state.merge(Immutable.fromJS({ alphaDetail: data, fetchingProperties: false }));
        }
      }
    },
    SET_ALPHA_DETAIL: (state: AlphasStoreElement, action: AlphasActionType) => {
      const alphaDetail = getAlphaDetail(state, action.payload);

      return state.merge(
        Immutable.fromJS({
          alphaDetail,
          showDetail: true,
          chartType: 'pnl',
          summaryOSPROD: Immutable.fromJS(getChartTable(alphaDetail)),
        })
      );
    },
    SET_ALPHA_PROPERTY: (state: AlphasStoreElement, action: AlphasActionType) => setAlphaProp(state, action.payload),
    SET_SINGLE_ALPHA_DETAIL: (state: AlphasStoreElement, action: AlphasActionType) => state.set('currentAlphaId', action.payload),
    SET_ALPHA_DETAIL_ASYNC: (state: AlphasStoreElement, action: any) => {
      let myAlphas;

      if (state.get('myAlphas') === null) {
        myAlphas = null;
      } else {
        const results = state.getIn(['myAlphas', 'results']);
        // @ts-ignore
        const alphaIndex = results && results.findIndex((alpha: any) => alpha.get('id') === action.payload.id);
        if (alphaIndex >= 0) {
          myAlphas =
            state.get('myAlphas') &&
            state.get('myAlphas').setIn(
              ['results', alphaIndex],
              Immutable.fromJS({
                ...action.payload,
                performanceAlphaFetching: false,
                performanceAlphaSummary: getAlphaSummary(),
                performanceAlphaLastDate: null,
              })
            );
        } else {
          myAlphas = state.get('myAlphas');
        }
      }
      let alphaDetail = state.get('alphaDetail');
      const alphaId = state.getIn(['alphaDetail', 'id']) || state.get('currentAlphaId');
      if (action.payload.id === alphaId) {
        alphaDetail = {
          ...action.payload,
          performanceAlphaFetching: false,
          performanceAlphaSummary: getAlphaSummary(),
          performanceAlphaLastDate: null,
        };
      }

      return state.merge(
        Immutable.fromJS({
          alphaDetail,
          fetchingData: false,
          myAlphas: myAlphas,
          summaryOSPROD: Immutable.fromJS(getChartTable(Immutable.fromJS(action.payload))),
        })
      );
    },
    SET_DETAIL_TAB: (state: AlphasStoreElement, action: AlphasActionType) =>
      state.merge(
        Immutable.fromJS({
          detailTabSelected: action.payload,
        })
      ),
    REMOVE_DETAIL: (state: AlphasStoreElement) =>
      state.merge(
        Immutable.fromJS({
          alphaDetail: null,
          showDetail: false,
          detailTabSelected: 0,
          chartData: null,
          recordsSets: null,
          hasComeFromAlphaPage: false,
          summaryTable: null,
          summaryOSPROD: null,
        })
      ),
    CHANGE_RECORD_SET_NAME: (state: AlphasStoreElement) => state.set('fetchingRecordSetsChart', true),
    SET_SELECTED_COLUMNS_ALPHAS: (state: AlphasStoreElement, action: AlphasActionType) =>
      state.set('selectedColumns', Immutable.fromJS(action.payload.selectedColumns)),
    RESET_SELECTED_COLUMNS_ALPHAS: (state: AlphasStoreElement) =>
      state.set('selectedColumns', Immutable.fromJS(initialSelectedColumnsAlphas)),
    REQUEST_ALPHAS_CHECK: (state: AlphasStoreElement) => state.set('fetchingCheck', true),
    RESPONSE_ALPHAS_CHECK_FAILURE: (state: AlphasStoreElement) => state.set('fetchingCheck', false),
    RESPONSE_ALPHAS_CHECK: (state: AlphasStoreElement, action: AlphasActionType) => {
      const results = state.getIn(['myAlphas', 'results']);
      // @ts-ignore
      const alphaIndex = results && results.findIndex((alpha: any) => alpha.get('id') === action.payload.alphaId);

      const currentAlphaId = state.getIn(['alphaDetail', 'id']);
      if (alphaIndex >= 0) {
        return state
          .set('fetchingCheck', false)
          .setIn(['alphaDetail', 'is', 'checks'], Immutable.fromJS(action.payload.json))
          .setIn(['myAlphas', 'results', alphaIndex, 'is', 'checks'], Immutable.fromJS(action.payload.json));
      } else if (action.payload.alphaId === currentAlphaId) {
        return state.set('fetchingCheck', false).setIn(['alphaDetail', 'is', 'checks'], Immutable.fromJS(action.payload.json));
      }

      return state.set('fetchingCheck', false);
    },
    RESPONSE_ALPHAS_CORRELATION_SUCCESS: createResponseAlphasCorrelationSuccessValues(
      'correlationSelf',
      'lastRunSelf',
      'fetchingCorrelation'
    ),
    RESPONSE_ALPHAS_CORRELATION_SUCCESS_POWER_POOL: createResponseAlphasCorrelationSuccessValues(
      'correlationPowerPool',
      'lastRunPowerPool',
      'fetchingCorrelationPowerPool'
    ),
    REQUEST_ALPHAS_CORRELATION_POWER_POOL: createRequestAlphasCorrelation('fetchingCorrelationPowerPool'),
    REQUEST_ALPHAS_CORRELATION: createRequestAlphasCorrelation('fetchingCorrelation'),
    RESPONSE_ALPHAS_CORRELATION_FAILURE: createResponseAlphasCorrelationFailure('fetchingCorrelation'),
    RESPONSE_ALPHAS_CORRELATION_FAILURE_POWER_POOL: createResponseAlphasCorrelationFailure('fetchingCorrelationPowerPool'),
    RESPONSE_ALPHAS_CORRELATION_PROD_SUCCESS: (state: AlphasStoreElement, action: AlphasActionType) => {
      const currentAlphaId = state.getIn(['alphaDetail', 'id']);
      if (action.payload.alphaId) {
        const results = state.getIn(['myAlphas', 'results']);

        const shouldSaveInAlphaDetail = action.payload.alphaId === currentAlphaId;
        // @ts-ignore
        const alphaIndex = results && results.findIndex((alpha: any) => alpha.get('id') === action.payload.alphaId);
        if (results && alphaIndex >= 0) {
          return state
            .setIn(['alphaDetail', 'correlationProd'], shouldSaveInAlphaDetail ? Immutable.fromJS(action.payload.json) : undefined)
            .setIn(['myAlphas', 'results', alphaIndex, 'correlationProd'], Immutable.fromJS(action.payload.json))
            .setIn(['myAlphas', 'results', alphaIndex, 'lastRunProd'], action.payload.date)
            .setIn(['alphaDetail', 'fetchingCorrelationProd'], false)
            .setIn(['alphaDetail', 'lastRunProd'], shouldSaveInAlphaDetail ? action.payload.date : undefined);
        } else {
          return state
            .setIn(['alphaDetail', 'correlationProd'], shouldSaveInAlphaDetail ? Immutable.fromJS(action.payload.json) : undefined)
            .setIn(['alphaDetail', 'fetchingCorrelationProd'], false)
            .setIn(['alphaDetail', 'lastRunProd'], shouldSaveInAlphaDetail ? action.payload.date : undefined);
        }
      }

      return state;
    },
    REQUEST_ALPHAS_CORRELATION_PROD: (state: AlphasStoreElement) => {
      if (state.get('alphaDetail')) {
        return state.setIn(['alphaDetail', 'fetchingCorrelationProd'], true);
      } else {
        return state;
      }
    },
    RESPONSE_ALPHAS_CORRELATION_PROD_FAILURE: (state: AlphasStoreElement, action: AlphasCorrelationFailureActionType) => {
      if (state.get('alphaDetail')) {
        return state
          .setIn(['alphaDetail', 'correlationStatusCode'], action.payload?.statusCode)
          .setIn(['alphaDetail', 'fetchingCorrelationProd'], false);
      } else {
        return state;
      }
    },
    RESPONSE_RECORDS_SET_CHART_SUCCESS: (state: AlphasStoreElement, action: AlphasActionType) => {
      const currentAlphaId = state.getIn(['alphaDetail', 'id']);
      if (action.payload.alphaId === currentAlphaId && action.payload.json && action.payload.json.schema && action.payload.json.records) {
        const chartType = state.get('chartType');
        const mappingChart: any = chartMapping;
        const getRecordSeries = mappingChart[chartType];
        const { osStartDate, testStartDate, validationStartDate, trainStartDate } = getStartDates(state);
        const schema = action.payload.json.schema;
        const records = action.payload.json.records;
        const series = getRecordSeries({
          chartDataSchema: schema,
          chartDataRecords: records,
          osStartDate,
          testStartDate,
          validationStartDate,
          trainStartDate,
          recordSet: chartType,
          isSuperAlpha: state.getIn(['alphaDetail', 'type']) === 'SUPER',
        });

        return state
          .set('fetchingRecordSetsChart', false)
          .set('chartData', Immutable.fromJS(action.payload.json))
          .setIn(['chartData', 'records'], Immutable.fromJS(series));
      } else {
        return state.set('fetchingRecordSetsChart', false);
      }
    },
    REQUEST_RECORDS_SET_CHART: (state: AlphasStoreElement) => state.set('fetchingRecordSetsChart', true),
    RESPONSE_RECORDS_SET_CHART_FAILURE: (state: AlphasStoreElement) => state.set('fetchingRecordSetsChart', false),
    RESPONSE_RECORDS_SET_SUMMARY_SUCCESS: (state: AlphasStoreElement, action: AlphasActionType) => {
      if (action.payload.alphaId && action.payload.json && action.payload.json.schema && action.payload.json.records) {
        const summaryTable = Immutable.fromJS(getChartTable(Immutable.fromJS({ is: { details: action.payload.json } })));

        return state.set('summaryTable', Immutable.fromJS(summaryTable)).set('fetchingRecordSetsSummary', false);
      } else {
        return state.set('fetchingRecordSetsSummary', false);
      }
    },
    REQUEST_RECORDS_SET_SUMMARY: (state: AlphasStoreElement) => state.set('fetchingRecordSetsSummary', true),
    RESPONSE_RECORDS_SET_SUMMARY_FAILURE: (state: AlphasStoreElement) => state.set('fetchingRecordSetsSummary', false),
    RESPONSE_RECORDS_SET_SUCCESS: (state: AlphasStoreElement, action: AlphasActionType) => {
      const currentAlphaId = state.getIn(['alphaDetail', 'id']);
      if (action.payload.alphaId === currentAlphaId) {
        const recordSet = action.payload.json;
        recordSet.results = action.payload.json.results.filter((metric: any) => metric.name !== 'is');

        return state.set('fetchingRecordSets', false).set('recordsSets', Immutable.fromJS(recordSet));
      } else {
        return state.set('fetchingRecordSets', false);
      }
    },
    REQUEST_RECORDS_SET: (state: AlphasStoreElement) => state.set('fetchingRecordSets', true),
    RESPONSE_RECORDS_SET_FAILURE: (state: AlphasStoreElement) => state.set('fetchingRecordSets', false),
    CLEAN_RECORD_SET: (state: AlphasStoreElement) => state.set('recordsSets', null),
    CLEAN_RECORD_SET_SUMMARY: (state: AlphasStoreElement) => state.set('summaryTable', null),
    CLEAN_RECORD_SET_CHART: (state: AlphasStoreElement) => state.set('chartData', null),
    REQUEST_ALPHAS_SUBMISSION: (state: AlphasStoreElement) => state.set('fetchingSubmitAlpha', true).set('fetchingCheck', true),
    RESPONSE_ALPHAS_SUBMISSION_FAILURE: (state: AlphasStoreElement) => state.set('fetchingSubmitAlpha', false).set('fetchingCheck', false),
    RESPONSE_ALPHAS_SUBMISSION_SUCCESS: (state: AlphasStoreElement) => state.set('fetchingSubmitAlpha', false).set('fetchingCheck', false),
    CHANGE_TYPE_MENU: (state: AlphasStoreElement, action: AlphasActionType) => state.set('type', action.payload),
    REQUEST_ALPHAS_COUNT: (state: AlphasStoreElement) => state.set('fetchNum', true),
    RESPONSE_ALPHAS_COUNT_FAILURE: (state: AlphasStoreElement) => state.set('fetchNum', false),
    RESPONSE_ALPHAS_COUNT_SUCCESS: (state: AlphasStoreElement, action: AlphasActionType) =>
      state
        .set('fetchNum', false)
        .set('numOfUnsubmitted', action.payload.results.unsubmitted)
        .set('numOfActive', action.payload.results.active)
        .set('numOfDecommissioned', action.payload.results.decommissioned),
    CLEAR_ALPHAS: (state: AlphasStoreElement) =>
      state.merge(
        Immutable.fromJS({
          myAlphas: null,
          alphaDetail: null,
          currentAlphaId: null,
          showDetail: false,
        })
      ),
    HAS_ENTERED_ALPHA_PAGE: (state: AlphasStoreElement) => state.set('hasComeFromAlphaPage', true),
    CHANGE_CHART_TYPE: (state: AlphasStoreElement, action: AlphasActionType) =>
      state.set('chartType', action.payload).set('fetchingRecordSetsChart', true),
    AUTHENTICATION_LOGOUT: () => initialState,
    CHANGE_PATH: (state: AlphasStoreElement, action: AlphasActionType) => state.set('alphasPath', action.payload),
    REQUEST_SELF_CORRELATION_TAG: (state: AlphasStoreElement) => state.set('fetchingSelfCorrelation', true),
    RESPONSE_SELF_CORRELATION_TAG_FAILURE: (state: AlphasStoreElement) => state.set('fetchingSelfCorrelation', false),
    RESPONSE_SELF_CORRELATION_TAG_SUCCESS: (state: AlphasStoreElement, action: AlphasActionType) =>
      state.set('fetchingSelfCorrelation', false).set('selfCorrelationData', Immutable.fromJS(action.payload)),
    REQUEST_ALPHAS_PERFORMANCE_COMPARISON: (state: AlphasStoreElement) => state.setIn(['alphaDetail', 'performanceAlphaFetching'], true),
    RESPONSE_ALPHAS_PERFORMANCE_COMPARISON_FAILURE: (state: AlphasStoreElement) =>
      state.setIn(['alphaDetail', 'performanceAlphaFetching'], false),
    RESPONSE_ALPHAS_PERFORMANCE_COMPARISON_SUCCESS: (state: AlphasStoreElement, action: AlphasActionType) => {
      const currentAlphaId = state.getIn(['alphaDetail', 'id']);
      const performanceDataPnl = Object.assign({}, action.payload.json.pnl);
      const performanceAlphaSummary = updateAlphasSummary(action.payload.json.score, 'score');
      const performanceAlphaDatafieldsScore = updateAlphasSummary(action.payload.json.datafieldsScore, 'datafieldsScore');
      const performanceAlphaWeeklyScore = updateAlphasSummary(action.payload.json.weeklyScore, 'weeklyScore');
      const performanceAlphaRobustnessScore = updateAlphasSummary(action.payload.json.robustnessScore, 'robustnessScore');
      const getRecordSeries = chartMapping['average-value-by-industry'];
      const { osStartDate, testStartDate, validationStartDate, trainStartDate } = getStartDates(state);
      const schema = action.payload.json.pnl.schema;
      const records = action.payload.json.pnl.records;
      const series = getRecordSeries({
        chartDataSchema: schema,
        chartDataRecords: records,
        osStartDate,
        testStartDate,
        trainStartDate,
        validationStartDate,
        recordSet: 'average-value-by-industry',
      });
      const competition = action.payload.json.competition ? Immutable.fromJS(action.payload.json.competition) : null;
      const partition = Immutable.fromJS(action.payload.json.partition ?? []);
      const colors = ['#f69526', '#4fb6f5'];
      performanceDataPnl.records = series.map((serie: any, index: any) => {
        serie.color = colors[index];

        return serie;
      });
      if (action.payload.alphaId && action.payload.json && action.payload.json.stats) {
        const performanceStats = Immutable.fromJS(
          formatPerformanceStats(action.payload.json.stats.before, action.payload.json.stats.after)
        );

        const performanceTable = Immutable.fromJS(
          formatPerformanceData(action.payload.json.yearlyStats.before, action.payload.json.yearlyStats.after)
        );
        const results = state.getIn(['myAlphas', 'results']);

        const shouldSaveInAlphaDetail = action.payload.alphaId === currentAlphaId;
        // @ts-ignore
        const alphaIndex = results && results.findIndex((alpha: any) => alpha.get('id') === action.payload.alphaId);
        if (results && alphaIndex >= 0) {
          return state
            .setIn(
              ['alphaDetail', 'performancePnl', 'chartData'],
              shouldSaveInAlphaDetail ? Immutable.fromJS(performanceDataPnl) : undefined
            )
            .setIn(['alphaDetail', 'performancePnl', 'chartType'], shouldSaveInAlphaDetail ? 'average-value-by-industry' : undefined)
            .setIn(['alphaDetail', 'performanceTable'], shouldSaveInAlphaDetail ? performanceTable : undefined)
            .setIn(['alphaDetail', 'performanceStats'], shouldSaveInAlphaDetail ? performanceStats : undefined)
            .setIn(
              ['myAlphas', 'results', alphaIndex, 'performancePnl', 'chartData'],
              shouldSaveInAlphaDetail ? Immutable.fromJS(performanceDataPnl) : undefined
            )
            .setIn(['myAlphas', 'results', alphaIndex, 'performancePnl', 'chartType'], 'average-value-by-industry')
            .setIn(['myAlphas', 'results', alphaIndex, 'performanceTable'], performanceTable)
            .setIn(['myAlphas', 'results', alphaIndex, 'performanceStats'], performanceStats)
            .setIn(['myAlphas', 'results', alphaIndex, 'performanceAlphaLastDate'], action.payload.date)
            .setIn(['alphaDetail', 'performanceAlphaFetching'], false)
            .setIn(['alphaDetail', 'performanceAlphaSummary'], Immutable.fromJS(performanceAlphaSummary))
            .setIn(['alphaDetail', 'performanceAlphaDatafieldsScore'], Immutable.fromJS(performanceAlphaDatafieldsScore))
            .setIn(['alphaDetail', 'performanceAlphaWeeklyScore'], Immutable.fromJS(performanceAlphaWeeklyScore))
            .setIn(['alphaDetail', 'performanceAlphaRobustnessScore'], Immutable.fromJS(performanceAlphaRobustnessScore))
            .setIn(['alphaDetail', 'performanceAlphaLastDate'], shouldSaveInAlphaDetail ? action.payload.date : undefined)
            .setIn(['alphaDetail', 'performancePartition'], partition)
            .setIn(['alphaDetail', 'performanceCompetition'], competition);
        } else {
          return state
            .setIn(
              ['alphaDetail', 'performancePnl', 'chartData'],
              shouldSaveInAlphaDetail ? Immutable.fromJS(performanceDataPnl) : undefined
            )
            .setIn(['alphaDetail', 'performancePnl', 'chartType'], shouldSaveInAlphaDetail ? 'average-value-by-industry' : undefined)
            .setIn(['alphaDetail', 'performanceTable'], shouldSaveInAlphaDetail ? performanceTable : undefined)
            .setIn(['alphaDetail', 'performanceStats'], shouldSaveInAlphaDetail ? performanceStats : undefined)
            .setIn(['alphaDetail', 'performanceAlphaFetching'], false)
            .setIn(['alphaDetail', 'performanceAlphaSummary'], Immutable.fromJS(performanceAlphaSummary))
            .setIn(['alphaDetail', 'performanceAlphaDatafieldsScore'], Immutable.fromJS(performanceAlphaDatafieldsScore))
            .setIn(['alphaDetail', 'performanceAlphaWeeklyScore'], Immutable.fromJS(performanceAlphaWeeklyScore))
            .setIn(['alphaDetail', 'performanceAlphaRobustnessScore'], Immutable.fromJS(performanceAlphaRobustnessScore))
            .setIn(['alphaDetail', 'performanceAlphaLastDate'], shouldSaveInAlphaDetail ? action.payload.date : undefined)
            .setIn(['alphaDetail', 'performancePartition'], partition)
            .setIn(['alphaDetail', 'performanceCompetition'], competition);
        }
      }

      return state.setIn(['alphaDetail', 'performanceAlphaFetching'], false);
    },
    REQUEST_SUPER_ALPHA_INFO: (state: AlphasStoreElement) => state.set('fetchSuperAlpha', true),
    RESPONSE_SUPER_ALPHA_INFO_FAILURE: (state: AlphasStoreElement) => state.set('fetchSuperAlpha', false),
    RESPONSE_SUPER_ALPHA_INFO_SUCCESS: (state: AlphasStoreElement, action: AlphasActionType) =>
      state.set('fetchSuperAlpha', false).setIn(['superAlphaProperties', 'name'], action.payload.name),
    MERGE_CORRELATION_TO_ALPHAS: (state: AlphasStoreElement) => state.setIn(['myAlphas', 'results'], setCorrelationInAlphas(state)),
    UPDATE_ACTIVE_BUTTON_CHART: (state: AlphasStoreElement, action: AlphasActionType) => state.set('allActive', action.payload),
    UPDATE_HIDE_BUTTON_CHART: (state: AlphasStoreElement, action: AlphasActionType) => state.set('hideActive', action.payload),
    INTRO_ALPHAS_STATE: () => initialStateForIntro,
    SET_ALPHA_TEST_PERIOD_SHOWN: (state: AlphasStoreElement, action: AlphasActionType) => {
      if (action.payload.alphaDetailPath?.[0] === 'alphas') {
        return state.setIn(
          [...action.payload.alphaDetailPath!.slice(1), 'isTestPeriodShown'],
          Immutable.fromJS(action.payload.isTestPeriodShown)
        );
      }
      return state;
    },
  },
  initialState
);

export default AlphasReducer;
